import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { MOCK_USERS } from '../../utils/mockData';
import { StatusToggle } from '../../components/ui/StatusToggle';
import { useToast } from '../../components/ui/Toast';
import { Plus, Edit, Shield, Search, Users as UsersIcon, ChevronLeft, ChevronRight, RefreshCw, KeyRound, Eye, EyeOff, CheckCircle2, Clock, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import type { MagentoSyncStatus } from '../../types';
const ITEMS_PER_PAGE = 10;
export function Users() {
  const navigate = useNavigate();
  const {
    showToast
  } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [users, setUsers] = useState(MOCK_USERS);
  const [showPasswordFor, setShowPasswordFor] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingUser, setSyncingUser] = useState<string | null>(null);
  const handleSearch = (val: string) => {
    setSearchQuery(val);
    setCurrentPage(1);
  };
  const filteredUsers = users.filter(user => {
    const q = searchQuery.toLowerCase();
    return user.name.toLowerCase().includes(q) || user.email.toLowerCase().includes(q) || user.username.toLowerCase().includes(q) || user.role.toLowerCase().includes(q);
  });
  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedUsers = filteredUsers.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);
  const startRow = (safePage - 1) * ITEMS_PER_PAGE + 1;
  const endRow = Math.min(safePage * ITEMS_PER_PAGE, filteredUsers.length);
  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'danger';
      case 'BRANCH':
        return 'success';
      default:
        return 'neutral';
    }
  };
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'Admin';
      case 'BRANCH':
        return 'Branch User';
      default:
        return role;
    }
  };
  const getSyncBadge = (status: MagentoSyncStatus) => {
    switch (status) {
      case 'SYNCED':
        return <Badge variant="success" size="sm" className="gap-1" data-id="element-1089">
            <CheckCircle2 className="w-3 h-3" data-id="element-1090" />
            Synced
          </Badge>;
      case 'PENDING':
        return <Badge variant="warning" size="sm" className="gap-1" data-id="element-1091">
            <Clock className="w-3 h-3" data-id="element-1092" />
            Pending
          </Badge>;
      case 'FAILED':
        return <Badge variant="danger" size="sm" className="gap-1" data-id="element-1093">
            <XCircle className="w-3 h-3" data-id="element-1094" />
            Failed
          </Badge>;
      case 'NOT_SYNCED':
        return <Badge variant="neutral" size="sm" className="gap-1" data-id="element-1095">
            <AlertCircle className="w-3 h-3" data-id="element-1096" />
            Not Synced
          </Badge>;
    }
  };
  const handleResetPassword = (userId: string, userName: string) => {
    showToast({
      type: 'success',
      title: 'Password Reset Link Sent',
      message: `A password reset link has been sent to ${userName}'s email.`
    });
  };
  const handleSyncAll = () => {
    setSyncingAll(true);
    setTimeout(() => {
      setUsers(prev => prev.map(u => ({
        ...u,
        magentoSyncStatus: 'SYNCED' as MagentoSyncStatus,
        lastMagentoSync: 'Just now'
      })));
      setSyncingAll(false);
      showToast({
        type: 'success',
        title: 'Magento Sync Complete',
        message: `All ${users.length} users have been synced to Magento.`
      });
    }, 2000);
  };
  const handleSyncUser = (userId: string, userName: string) => {
    setSyncingUser(userId);
    setTimeout(() => {
      setUsers(prev => prev.map(u => u.id === userId ? {
        ...u,
        magentoSyncStatus: 'SYNCED' as MagentoSyncStatus,
        lastMagentoSync: 'Just now'
      } : u));
      setSyncingUser(null);
      showToast({
        type: 'success',
        title: 'User Synced',
        message: `${userName} has been synced to Magento.`
      });
    }, 1200);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    ease: 'easeOut'
  }} className="space-y-6 pb-12" data-id="element-1097">
      <div className="flex items-center justify-between" data-id="element-1098">
        <div data-id="element-1099">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-1100">Users & Access</h1>
          <p className="text-slate-500" data-id="element-1101">
            Manage user accounts, credentials, and Magento sync
          </p>
        </div>
        <div className="flex gap-3" data-id="element-1102">
          <Button variant="outline" onClick={handleSyncAll} disabled={syncingAll} data-id="element-1103">
            {syncingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" data-id="element-1104" /> : <RefreshCw className="w-4 h-4 mr-2" data-id="element-1105" />}
            {syncingAll ? 'Syncing...' : 'Sync All to Magento'}
          </Button>
          <Button variant="primary" onClick={() => {}} data-id="element-1106">
            <Plus className="w-4 h-4 mr-2" data-id="element-1107" />
            Create User
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm" data-id="element-1108">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1109" />
        <input type="text" placeholder="Search by name, username, email, or role..." value={searchQuery} onChange={e => handleSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 placeholder:text-slate-400 bg-white" data-id="element-1110" />
      </div>

      <div className="rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden" data-id="element-1111">
        <Table data-id="element-1112">
          <TableHeader data-id="element-1113">
            <TableRow data-id="element-1114">
              <TableHead data-id="element-1115">User</TableHead>
              <TableHead data-id="element-1116">Username</TableHead>
              <TableHead data-id="element-1117">Password</TableHead>
              <TableHead data-id="element-1118">Role</TableHead>
              <TableHead data-id="element-1119">Magento Sync</TableHead>
              <TableHead data-id="element-1120">Status</TableHead>
              <TableHead data-id="element-1121">Last Login</TableHead>
              <TableHead className="text-right" data-id="element-1122">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-id="element-1123">
            {paginatedUsers.length > 0 ? paginatedUsers.map(user => <TableRow key={user.id} data-id="element-1124">
                  <TableCell data-id="element-1125">
                    <div className="flex items-center gap-3" data-id="element-1126">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600" data-id="element-1127">
                        {user.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div data-id="element-1128">
                        <div className="font-medium text-slate-900" data-id="element-1129">
                          {user.name}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-1130">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell data-id="element-1131">
                    <code className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-xs font-mono border border-slate-200" data-id="element-1132">
                      {user.username}
                    </code>
                  </TableCell>
                  <TableCell data-id="element-1133">
                    <div className="flex items-center gap-2" data-id="element-1134">
                      <code className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-xs font-mono border border-slate-200" data-id="element-1135">
                        {showPasswordFor === user.id ? user.password || '••••••••' : '••••••••'}
                      </code>
                      <button onClick={() => setShowPasswordFor(showPasswordFor === user.id ? null : user.id)} className="text-slate-400 hover:text-slate-600 transition-colors" title={showPasswordFor === user.id ? 'Hide password' : 'Show password'} data-id="element-1136">
                        {showPasswordFor === user.id ? <EyeOff className="w-3.5 h-3.5" data-id="element-1137" /> : <Eye className="w-3.5 h-3.5" data-id="element-1138" />}
                      </button>
                    </div>
                  </TableCell>
                  <TableCell data-id="element-1139">
                    <Badge variant={getRoleBadgeVariant(user.role)} size="sm" data-id="element-1140">
                      {getRoleLabel(user.role)}
                    </Badge>
                  </TableCell>
                  <TableCell data-id="element-1141">
                    <div className="flex items-center gap-2" data-id="element-1142">
                      {getSyncBadge(user.magentoSyncStatus)}
                      {user.magentoSyncStatus !== 'SYNCED' && <button onClick={() => handleSyncUser(user.id, user.name)} disabled={syncingUser === user.id} className="text-teal-600 hover:text-teal-700 transition-colors disabled:opacity-50" title="Sync to Magento" data-id="element-1143">
                          {syncingUser === user.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" data-id="element-1144" /> : <RefreshCw className="w-3.5 h-3.5" data-id="element-1145" />}
                        </button>}
                    </div>
                    {user.lastMagentoSync && <p className="text-[10px] text-slate-400 mt-0.5" data-id="element-1146">
                        {user.lastMagentoSync}
                      </p>}
                  </TableCell>
                  <TableCell data-id="element-1147">
                    <StatusToggle checked={user.status === 'ACTIVE'} onChange={checked => {
                setUsers(prev => prev.map(u => u.id === user.id ? {
                  ...u,
                  status: checked ? 'ACTIVE' : 'INACTIVE'
                } : u));
              }} data-id="element-1148" />
                  </TableCell>
                  <TableCell className="text-sm text-slate-600" data-id="element-1149">
                    {user.lastLogin || 'Never'}
                  </TableCell>
                  <TableCell className="text-right" data-id="element-1150">
                    <div className="flex items-center justify-end gap-1" data-id="element-1151">
                      <button onClick={() => handleResetPassword(user.id, user.name)} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50 rounded transition-colors" title="Reset Password" data-id="element-1152">
                        <KeyRound className="w-3 h-3" data-id="element-1153" />
                        Reset
                      </button>
                      <Button variant="ghost" size="sm" onClick={() => {}} data-id="element-1154">
                        <Edit className="w-3 h-3 mr-2" data-id="element-1155" />
                        Edit
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>) : <TableRow data-id="element-1156">
                <TableCell colSpan={8} className="h-40 text-center" data-id="element-1157">
                  <div className="flex flex-col items-center justify-center gap-2" data-id="element-1158">
                    <UsersIcon className="w-10 h-10 text-slate-300" data-id="element-1159" />
                    <p className="text-sm font-medium text-slate-900" data-id="element-1160">
                      No users found
                    </p>
                    <p className="text-sm text-slate-500" data-id="element-1161">
                      {searchQuery ? `No users match "${searchQuery}"` : 'No users have been added yet'}
                    </p>
                  </div>
                </TableCell>
              </TableRow>}
          </TableBody>
        </Table>

        {/* Pagination */}
        {filteredUsers.length > 0 && <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100" data-id="element-1162">
            <p className="text-sm text-slate-500" data-id="element-1163">
              Showing{' '}
              <span className="font-medium text-slate-700" data-id="element-1164">{startRow}</span> to{' '}
              <span className="font-medium text-slate-700" data-id="element-1165">{endRow}</span> of{' '}
              <span className="font-medium text-slate-700" data-id="element-1166">
                {filteredUsers.length}
              </span>{' '}
              rows
            </p>
            <div className="flex items-center gap-2" data-id="element-1167">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage <= 1} data-id="element-1168">
                <ChevronLeft className="w-4 h-4 mr-1" data-id="element-1169" />
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} data-id="element-1170">
                Next
                <ChevronRight className="w-4 h-4 ml-1" data-id="element-1171" />
              </Button>
            </div>
          </div>}
      </div>
    </motion.div>;
}